/**
 * Tujuan: About page — summary, education, internship experience, tech and tool stacks, CV download
 * Caller: App.js route /about
 * Dependensi: Techstack, Toolstack components, react-icons, resume PDF, portfolio PDF assets
 * Main Functions: About()
 * Side Effects: IntersectionObserver for fade-in animations (registered via useEffect, cleaned up on unmount)
 */
import React, { useEffect, useRef } from "react";
import { Container } from "react-bootstrap";
import Techstack from "../components/Techstack";
import Toolstack from "../components/Toolstack";
import { FaDownload } from "react-icons/fa";
import resumePDF from "../assets/Resume - Nabilah Putri.pdf";
import portfolioPDF from "../assets/PORTFOLIO.pdf";

function About() {
  const sectionRef = useRef(null);

  // Scroll-in animation via IntersectionObserver — cleaned up on unmount
  useEffect(() => {
    const elements = sectionRef.current?.querySelectorAll(".fade-in, .entry-block");
    if (!elements) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("show");
          }
        });
      },
      { threshold: 0.1 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <Container fluid className="about-section" ref={sectionRef}>
      <Container className="section-container">

        {/* PAGE HEADING */}
        <div className="text-center mb-5 fade-in">
          <h1>
            About <span className="blue">Me</span>
          </h1>
          <div className="about-cv-buttons">
            <a href={resumePDF} target="_blank" rel="noopener noreferrer" className="btn-primary">
              <FaDownload /> Download CV / Resume
            </a>
            <a href={portfolioPDF} target="_blank" rel="noopener noreferrer" className="btn-outline">
              <FaDownload /> Download Portfolio
            </a>
          </div>
        </div>

        {/* SUMMARY */}
        <div className="about-card fade-in">
          <h4 className="about-subtitle">Summary</h4>
          <p className="about-text">
            Data Analyst with hands-on experience in exploratory data analysis (EDA), data visualization,
            and dashboard development using Python and Tableau. Skilled in transforming complex datasets into
            actionable insights, with project experience spanning energy analysis, market trends, and e-commerce data warehousing.
          </p>
        </div>

        {/* ================= EDUCATION ================= */}
        <h3 className="about-section-title fade-in">Education</h3>

        <div className="entry-group">

          <div className="entry-block">
            <span className="entry-date">Nov 2025 — Present</span>
            <h5>Binus University Graduate Program</h5>
            <p className="entry-degree">Master of Information System Management</p>
            <p>
              Digital Business, Business Intelligence &amp; Analytics — covering Database, Business Analysis, and Product Management.
            </p>
          </div>

          <div className="entry-block">
            <span className="entry-date">Sep 2019 — Nov 2023</span>
            <h5>Binus University</h5>
            <p className="entry-degree">Bachelor of Information Systems</p>
            <p>
              Streaming E-Business — covering Database, UI/UX Design, Business Analysis, Product Management, and Programming.
            </p>
          </div>

        </div>

        {/* ================= INTERNSHIP EXPERIENCE ================= */}
        <h3 className="about-section-title fade-in">Internship Experience</h3>

        <div className="entry-group">

          <div className="entry-block">
            <span className="entry-date">Sep 2022 — Feb 2023</span>
            <h5>Telkom Indonesia</h5>
            <p className="entry-degree">UI/UX Designer Intern</p>
            <p className="entry-location">Jakarta, Indonesia</p>
            <ul>
              <li>Conducted user research to identify needs, pain points, and behavioral patterns for web applications</li>
              <li>Developed user personas and journey maps to translate business requirements into design solutions</li>
              <li>Designed and optimised 10+ interface pages to improve usability and overall user experience</li>
              <li>Created data visualisations and reports to communicate insights to stakeholders</li>
            </ul>
          </div>

          <div className="entry-block">
            <span className="entry-date">Feb 2022 — Aug 2022</span>
            <h5>PT. Media Televisi Indonesia (Metro TV)</h5>
            <p className="entry-degree">IT Development Intern</p>
            <p className="entry-location">Jakarta, Indonesia</p>
            <ul>
              <li>Collected and evaluated user and system requirements to support application development</li>
              <li>Translated abstract ideas into user personas, storyboards, and structured feature concepts</li>
              <li>Developed wireframes, mockups, and prototypes defining interface design and system flow</li>
              <li>Collaborated with developers and product owners to align visual design with functional requirements</li>
              <li>Identified usability issues and improved experience through iterative design</li>
            </ul>
          </div>

        </div>

        {/* TECH STACK */}
        <h3 className="section-title-center fade-in">
          Technologies I've <span>Worked With</span>
        </h3>
        <div className="fade-in">
          <Techstack />
        </div>

        {/* TOOL STACK */}
        <h3 className="section-title-center fade-in">
          <span>Tools</span> I Use
        </h3>
        <div className="fade-in">
          <Toolstack />
        </div>

      </Container>
    </Container>
  );
}

export default About;
